"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  fetchDashboardData,
  fetchDashboardStage1,
  fetchDashboardStage2,
  fetchDashboardStage3,
} from "../components/requests/dashboard-data";

const DashboardDataContext = createContext(null);

export const DashboardDataProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const inFlightRef = useRef({ full: null, staged: null });
  const dataScopeRef = useRef("empty");
  const dataRef = useRef(null);
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const loadFullData = useCallback(
    async (force = false) => {
      if (!force && dataScopeRef.current === "full") {
        return dataRef.current;
      }

      if (inFlightRef.current.full) {
        return inFlightRef.current.full;
      }

      setStatus("loading");
      setError(null);

      const request = fetchDashboardData()
        .then((payload) => {
          setData((prev) => ({ ...prev, ...payload }));
          dataScopeRef.current = "full";
          setStatus("success");
          return payload;
        })
        .catch((err) => {
          setStatus("error");
          setError(err);
          throw err;
        })
        .finally(() => {
          inFlightRef.current.full = null;
        });

      inFlightRef.current.full = request;
      return request;
    },
    []
  );

  /** Load in order: (1) Portfolio allocation + track summary, (2) Growth of $1 + 3 boxes, (3) rest */
  const loadStagedData = useCallback(async () => {
    if (dataScopeRef.current === "full") {
      return dataRef.current;
    }

    if (inFlightRef.current.staged) {
      return inFlightRef.current.staged;
    }

    setStatus("loading");
    setError(null);

    const runStages = async () => {
      try {
        const payload1 = await fetchDashboardStage1();
        setData((prev) => ({ ...prev, ...payload1 }));

        const payload2 = await fetchDashboardStage2();
        setData((prev) => ({ ...prev, ...payload2 }));

        const payload3 = await fetchDashboardStage3();
        setData((prev) => ({ ...prev, ...payload3 }));

        dataScopeRef.current = "full";
        setStatus("success");
        return dataRef.current;
      } catch (err) {
        setStatus("error");
        setError(err);
        throw err;
      } finally {
        inFlightRef.current.staged = null;
      }
    };

    const request = runStages();
    inFlightRef.current.staged = request;
    return request;
  }, []);

  const prefetch = useCallback(() => loadStagedData(), [loadStagedData]);
  const refresh = useCallback(() => loadFullData(true), [loadFullData]);

  // Prefetch dashboard data on app load so it's cached when user navigates to Dashboard
  useEffect(() => {
    prefetch();
  }, [prefetch]);

  // When user navigates to Dashboard, ensure data is loaded (progressive: homepage first, then supplement)
  useEffect(() => {
    if (pathname === "/dashboard") {
      prefetch();
    }
  }, [pathname, prefetch]);

  const value = useMemo(
    () => ({
      data,
      status,
      error,
      prefetch,
      refresh,
    }),
    [data, status, error, prefetch, refresh]
  );

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
};

export const useDashboardData = () => {
  const context = useContext(DashboardDataContext);
  if (!context) {
    throw new Error("useDashboardData must be used within DashboardDataProvider");
  }
  return context;
};
